import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box, Grid } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { useAppContext } from '../app-context';

type HeaderProps = {
	input: React.ReactNode;
	filters: React.ReactNode;
};

const styles = cssMap({
	base: {
		position: 'sticky',
		top: '0',
		backgroundColor: token('elevation.surface'),
		width: '100%',
		backgroundClip: 'padding-box',
		zIndex: 200,
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	keyline: {
		borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	},
	grid: {
		gridTemplateAreas: '"input ." "filters filters"',
		gridTemplateColumns: '65fr 35fr',
		paddingBottom: token('space.250'),
	},
	inputOnlyGrid: {
		gridTemplateAreas: '"input ."',
	},
	paddingTop: {
		paddingTop: token('space.300'),
	},
	inputGridArea: {
		gridArea: 'input',
		display: 'flex',
		alignItems: 'center',
	},
	filtersGridArea: {
		gridArea: 'filters',
	},
});

export const Header = ({ input, filters }: HeaderProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const hideFilters = fg('rovo-search-team-eu-26-fps-refresh');

	if (hideFilters && isNav4Enabled) {
		return <VisuallyHidden>{input}</VisuallyHidden>;
	}

	return (
		<>
			<Box xcss={cx(styles.base, styles.keyline)} testId="search-page-header">
				<Box xcss={styles.contentContainer}>
					<Grid
						xcss={cx(
							styles.grid,
							!isNav4Enabled && styles.paddingTop,
							hideFilters && styles.inputOnlyGrid,
						)}
						columnGap="space.200"
						rowGap={hideFilters ? 'space.0' : 'space.200'}
					>
						{!isNav4Enabled && <Box xcss={styles.inputGridArea}>{input}</Box>}
						{!hideFilters && <Box xcss={styles.filtersGridArea}>{filters}</Box>}
					</Grid>
				</Box>
			</Box>
			{isNav4Enabled && <VisuallyHidden>{input}</VisuallyHidden>}
		</>
	);
};
